import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  FprActiveStage,
  FprErrorType,
  FprState,
  IFprApiData,
  ICprPdpCmsData,
  ICprCheckPageCmsData,
  FprCmsStatus,
} from "../interfaces/fpr.interface";

const initialState: FprState = {
  activeStage: "fpr-clp",
  feedbackState: "NONE",
  apiData: null,
  errorType: null,
  errorStatusCode: null,
  errorMessage: null,
  errorCode: null,
  shouldRefetch: false,
  cprPdpCmsData: null,
  cprCheckPageCmsData: null,
  cmsStatus: "idle",
  customerType: null,
  loader: {
    isVisible: true,
    title: "",
    description: "",
  },
};

const fprSlice = createSlice({
  name: "fpr",
  initialState,
  reducers: {
    /** Switch the active stage — drives which stage component is mounted. */
    setActiveStage: (state, action: PayloadAction<FprActiveStage>) => {
      state.activeStage = action.payload;
    },

    /** Controls visibility of Exit and Feedback modals */
    setFeedbackState: (state, action: PayloadAction<"NONE" | "EXIT" | "FEEDBACK">) => {
      state.feedbackState = action.payload;
    },

    /** Persist normalised EMIPOD API response into the journey state. */
    setFprApiData: (state, action: PayloadAction<IFprApiData>) => {
      state.apiData = action.payload;
      // Clear any previous error when fresh data arrives
      state.errorType = null;
      state.errorStatusCode = null;
      state.errorMessage = null;
      state.errorCode = null;
    },

    // customer type
    setCustomerType: (state, action: PayloadAction<"NTB" | "PTB" | "ETB" | null>) => {
      state.customerType = action.payload;
    },

    /** Record an error state (shown in the fpr-error stage). */
    setFprError: (
      state,
      action: PayloadAction<{
        errorType: FprErrorType;
        statusCode?: number | null;
        errorCode?: number | string | null;
        errorMessage?: string | null;
      }>
    ) => {
      state.errorType = action.payload.errorType;
      state.errorStatusCode = action.payload.statusCode ?? null;
      state.errorMessage = action.payload.errorMessage ?? null;
      state.errorCode = action.payload.errorCode ?? null;
      state.activeStage = "fpr-error";
    },

    /** Toggle re-fetch trigger (e.g. after returning from PG). */
    setShouldRefetch: (state, action: PayloadAction<boolean>) => {
      state.shouldRefetch = action.payload;
    },

    /** Store PDP (GetCprLayout) CMS data delivered from the Server Component. */
    setCprPdpCmsData: (state, action: PayloadAction<ICprPdpCmsData | null>) => {
      state.cprPdpCmsData = action.payload;
    },

    /** Store Check-page (GetCprCheckLayout) CMS data delivered from the Server Component. */
    setCprCheckPageCmsData: (state, action: PayloadAction<ICprCheckPageCmsData | null>) => {
      state.cprCheckPageCmsData = action.payload;
    },

    /** Update CMS fetch lifecycle status. */
    setFprCmsStatus: (state, action: PayloadAction<FprCmsStatus>) => {
      state.cmsStatus = action.payload;
    },

    /** Show global spinning loader with optional content. */
    showFprLoader: (
      state,
      action: PayloadAction<{ title?: string; description?: string } | undefined>
    ) => {
      state.loader ??= {
        isVisible: true,
        title: "",
        description: "",
      };
      state.loader.isVisible = true;
      state.loader.title = action.payload?.title ?? "";
      state.loader.description = action.payload?.description ?? "";
    },

    /** Hide global spinning loader. */
    hideFprLoader: (state) => {
      state.loader ??= {
        isVisible: true,
        title: "",
        description: "",
      };
      state.loader.isVisible = false;
    },

    /** Clear error state without resetting the whole journey (e.g. after dismissing error). */
    clearFprError: (state) => {
      state.errorType = null;
      state.errorStatusCode = null;
      state.errorMessage = null;
      state.errorCode = null;
    },

    /** Reset journey state back to initial (e.g. on logout). */
    resetFprState: () => initialState,
  },
});

export const {
  setActiveStage,
  setFeedbackState,
  setFprApiData,
  setCustomerType,
  setFprError,
  clearFprError,
  setShouldRefetch,
  setCprPdpCmsData,
  setCprCheckPageCmsData,
  setFprCmsStatus,
  showFprLoader,
  hideFprLoader,
  resetFprState,
} = fprSlice.actions;

export default fprSlice.reducer;
